import { useEffect, useState, type FormEvent } from 'react';
import type { ApiClient, Identity, MeResponse } from '../api';
import { formatDate, renderError, statusTag } from './shared';

export function AccountView({ client }: { client: ApiClient }) {
  const [account, setAccount] = useState<MeResponse | null>(null);
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void Promise.all([client.getMe(), client.getMyIdentities()])
      .then(([nextAccount, nextIdentities]) => {
        setAccount(nextAccount);
        setDisplayName(nextAccount.displayName ?? '');
        setAvatarUrl(nextAccount.avatarUrl ?? '');
        setIdentities(nextIdentities.identities);
      })
      .catch(setError);
  }, [client]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updated = await client.updateMe({
        displayName: displayName.trim() || undefined,
        avatarUrl: avatarUrl.trim() || undefined,
      });
      setAccount(updated);
      setDisplayName(updated.displayName ?? '');
      setAvatarUrl(updated.avatarUrl ?? '');
    } catch (nextError) {
      setError(nextError);
    } finally {
      setSaving(false);
    }
  }

  if (error) return <>{renderError(error)}</>;
  if (!account) return <p className="muted">Loading account...</p>;

  return (
    <div>
      <div className="page-heading">
        <div>
          <p className="eyebrow">DeskTouch identity</p>
          <h2>My account</h2>
          <p className="muted">Your profile and sign-in methods for DeskTouch.</p>
        </div>
        {statusTag(account.status)}
      </div>
      <div className="grid-two">
        <section className="panel">
          <h2>Profile</h2>
          <form className="stack" onSubmit={save}>
            <label>
              Email
              <input value={account.email ?? 'Not available'} readOnly />
            </label>
            <label>
              Display name
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={120} />
            </label>
            <label>
              Avatar URL
              <input type="url" value={avatarUrl} onChange={(event) => setAvatarUrl(event.target.value)} placeholder="https://..." />
            </label>
            <button className="primary" disabled={saving} type="submit">
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          </form>
        </section>
        <section className="panel">
          <h2>Sign-in methods</h2>
          <p className="muted">These methods are managed by Logto. Keep at least one linked method.</p>
          <ul className="identity-list">
            {identities.map((identity) => (
              <li key={identity.id}>
                <strong>{identity.provider}</strong>
                <span>{identity.email ?? 'Provider account'}</span>
                {identity.isPrimary && <span className="tag active">Primary</span>}
              </li>
            ))}
          </ul>
          {identities.length === 0 && <p className="muted">No linked methods have been mirrored yet.</p>}
          <p className="muted account-note">To add Google or email, sign in through Logto again and use its account-linking flow.</p>
        </section>
      </div>
      <section className="panel account-meta">
        <h2>Account details</h2>
        <dl className="row">
          <dt>Plan</dt><dd>{account.subscription.plan}</dd>
          <dt>Member since</dt><dd>{formatDate(account.createdAt)}</dd>
          <dt>Last sign-in</dt><dd>{formatDate(account.lastLoginAt)}</dd>
          <dt>Account ID</dt><dd><code>{account.id}</code></dd>
        </dl>
      </section>
    </div>
  );
}